/**
 * Public homepage — marketplace redesign, Feb 2026.
 *
 * Follows the CasperDomains design system (`styles/casperdomains.css`).
 * Sections:
 *   1. Sticky navy hero with search + orbital art
 *   2. Featured domains (live from /api/listings/featured)
 *   3. Category cards (Popular, 3-letter, Premium)
 *   4. Buyer benefits (split, illustration + feature list)
 *   5. Seller / investor pitch (split, reverse)
 *   6. Domain columns (Featured .com / Short brandable / Two-word)
 *   7. Trust benefits (escrow, transfer, refund)
 *   8. CTA search strip
 *   9. Lead capture (kept from previous impl, restyled)
 *
 * Admin sign-in stays low-visibility: keyboard shortcut Cmd/Ctrl + . jumps
 * to /login; the footer also has a discreet "Admin" link.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, ArrowUpRight, ShieldCheck, Clock, Sparkles,
  Globe, Award, Zap, TrendingUp, CheckCircle2, MessageCircle,
} from "lucide-react";
import { api, submitLead, fetchLatestSold, searchListings, fetchMarketplaceStats } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { gradientFromDomain } from "@/lib/gradient";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import Seo from "@/components/Seo";
import CompareTray, { PinButton } from "@/components/marketplace/CompareTray";
import SnitcherEmbed from "@/components/marketplace/SnitcherEmbed";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";

export default function PublicHome() {
  const nav = useNavigate();
  // Brand comes from the app-wide BrandingProvider which hydrates from
  // localStorage synchronously — no first-load flash for returning
  // visitors, and a same-frame update as soon as /api/brand resolves.
  const brand = useBranding();
  const [featured, setFeatured] = useState([]);
  const [latestSold, setLatestSold] = useState(null);
  const [mpStats, setMpStats] = useState(null);
  const [soldCases, setSoldCases] = useState([]);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = show featured; array = show results
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [params] = useSearchParams();

  // Sync ?category=X in the URL (from the header mega-menu) to the active
  // filter chip on mount so buyers land inside a pre-filtered grid.
  useEffect(() => {
    const c = params.get("category");
    if (c) setActiveCategory(c);
  }, [params]);

  useEffect(() => {
    // Seed a stable session id so the lead form carries one → Hot Leads
    // page can attribute this browser to an email once they write in.
    if (!localStorage.getItem("sniper_sid")) {
      localStorage.setItem(
        "sniper_sid",
        Math.random().toString(36).slice(2) + Date.now().toString(36),
      );
    }
    // Baseline "featured" strip when no query is active.
    api.get("/listings/featured", { params: { limit: 30 } })
      .then((r) => setFeatured(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFeatured([]));
    api.get("/listings/categories")
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
    fetchLatestSold().then((r) => setLatestSold(r?.listing || null)).catch(() => {});
    fetchMarketplaceStats().then(setMpStats).catch(() => {});
    api.get("/listings/sold-case-studies", { params: { limit: 6 } })
      .then((r) => setSoldCases(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setSoldCases([]));

    // Discreet admin escape hatch: Cmd/Ctrl + . jumps to /login.
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault();
        nav("/login");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nav]);

  // Debounced server-side search. Hits /api/listings/search which scans the
  // full active inventory (750+ listings) rather than the 30 loaded upfront.
  const q = query.trim().toLowerCase();
  useEffect(() => {
    if (!q && !activeCategory) {
      setSearchResults(null);
      setSearchTotal(0);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/listings/search", { params: { q, category: activeCategory, limit: 60 } })
        .then((r) => {
          setSearchResults(r.data?.items || []);
          setSearchTotal(r.data?.total || 0);
        })
        .catch(() => { setSearchResults([]); setSearchTotal(0); })
        .finally(() => setSearching(false));
    }, 250); // 250ms debounce
    return () => clearTimeout(t);
  }, [q, activeCategory]);

  // When search is active, split the filtered list into 3 balanced columns
  // so the featured section keeps its shape.
  const [colA, colB, colC] = useMemo(() => {
    const source = searchResults || featured;
    const a = [], b = [], c = [];
    source.forEach((d, i) => {
      if (i % 3 === 0) a.push(d);
      else if (i % 3 === 1) b.push(d);
      else c.push(d);
    });
    return [a, b, c];
  }, [featured, searchResults]);

  const runSearch = (e) => {
    e.preventDefault();
    // Query already filters live via state — pressing enter just anchors the
    // viewport to the results so the buyer can see what they're looking at.
    const el = document.getElementById("featured");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const clearSearch = () => { setQuery(""); setActiveCategory(""); };

  // Editorial hero band — pick the busiest category that also has an
  // admin-uploaded hero image. Falls back to nothing (band is hidden)
  // when no category is set up yet.
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const topCategory = useMemo(() => {
    const withHero = (categories || []).filter((c) => c.hero_url && c.count > 0);
    if (withHero.length === 0) return null;
    const top = [...withHero].sort((a, b) => (b.count || 0) - (a.count || 0))[0];
    // Map "Real Estate" → "real-estate" for the /category/:slug route.
    const slug = String(top.key || "").toLowerCase().replace(/\s+/g, "-");
    const heroFull = top.hero_url?.startsWith("/api/") ? `${backendUrl}${top.hero_url}` : top.hero_url;
    // Editorial one-liners per category — mirrors the /category/:slug page
    // so the band feels like a preview of what buyers get after the click.
    const TAGLINES = {
      "AI":          "The next wave of AI infrastructure lives here.",
      "SaaS":        "Two-syllable, brand-first .coms for SaaS founders.",
      "Finance":     "Trust-first names for fintech and payments.",
      "Real Estate": "Category-defining names for prop-tech.",
      "Crypto":      "Sharp, memorable domains for exchanges and DeFi.",
      "Health":      "Warm, credible names for wellness and care.",
      "Commerce":    "DTC-ready domains for e-commerce brands.",
      "Media":       "Editorial names for publishers and creators.",
      "Travel":      "Aspirational one-worders for hospitality.",
      "Other":       "One-of-one gems that don't fit a bucket.",
    };
    return {
      key: top.key,
      slug,
      count: top.count,
      hero_url: heroFull,
      focal_x: typeof top.focal_x === "number" ? top.focal_x : 50,
      focal_y: typeof top.focal_y === "number" ? top.focal_y : 50,
      tagline: TAGLINES[top.key] || "The busiest corner of the shelf right now.",
    };
  }, [categories, backendUrl]);

  return (
    <div className="cd-marketplace" style={{ background: "var(--cd-white)", color: "var(--cd-black)", fontFamily: "var(--cd-font)" }}>
      <Seo
        title={`${brand.brand_name} · Premium domains for the next big idea`}
        description={`${brand.brand_name} is a curated brokerage for premium expired domains. Hand-picked inventory, priced to move, checkout in one minute via Stripe or Escrow.`}
        type="website"
        siteName={brand.brand_name}
        image={brand.logo_url}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: brand.brand_name,
          url: typeof window !== "undefined" ? window.location.origin : "",
          logo: brand.logo_url,
          description: `${brand.brand_name} is a curated brokerage for premium expired domains.`,
        }}
      />
      <SiteHeader />

      {/* ===================== 1. HERO ===================== */}
      <section className="cd-container cd-hero" aria-label="Find your domain">
        <div className={`cd-hero__panel ${brand.hero_media_url ? "cd-hero__panel--media" : ""}`}>
          {/* Optional admin-provided background media (image OR video).
              When present it replaces the aurora blobs entirely so the
              broker can drop in a custom photo/loop without the ambient
              gradients competing. Video/image is auto-detected from the
              URL extension; ambiguous URLs fall back to <img>. */}
          {brand.hero_media_url && (
            <div className="cd-hero__media" aria-hidden="true">
              {/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(brand.hero_media_url) ? (
                <video
                  src={brand.hero_media_url}
                  autoPlay muted loop playsInline
                  data-testid="hero-media-video"
                />
              ) : (
                <img
                  src={brand.hero_media_url}
                  alt=""
                  data-testid="hero-media-image"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              <span className="cd-hero__media-scrim" />
            </div>
          )}

          {!brand.hero_media_url && (
            /* Ambient aurora — 3 soft blobs behind a grid + noise overlay */
            <div className="cd-hero__aurora" aria-hidden="true">
              <span className="cd-hero__aurora-blob cd-hero__aurora-blob--a" />
              <span className="cd-hero__aurora-blob cd-hero__aurora-blob--b" />
              <span className="cd-hero__aurora-blob cd-hero__aurora-blob--c" />
              <span className="cd-hero__aurora-grid" />
              <span className="cd-hero__aurora-noise" />
            </div>
          )}

          <div className="cd-hero__copy cd-rise">
            <h1 className="cd-display" style={{ color: "#fff" }} data-testid="hero-headline">
              {(brand.hero_headline_prefix || "The").trim()}{" "}
              <span className="cd-hero__accent-word">
                <RotatingWord
                  words={
                    (brand.hero_rotating_words || "")
                      .split(",")
                      .map((w) => w.trim())
                      .filter(Boolean)
                  }
                />
              </span>
              <br />
              {(brand.hero_headline_suffix || "lives at the perfect domain.").trim()}
            </h1>
            <p>
              {brand.hero_subheading
                ? brand.hero_subheading
                : (
                  <>
                    Hand-picked expired premium domains, transferred in{" "}
                    <strong style={{ color: "#fff" }}>under 24 hours</strong>, secured by Stripe
                    or Escrow. One broker, one desk, one price. <em style={{ color: "var(--cd-cyan-400)", fontStyle: "normal" }}>No marketplace fees on top</em>.
                  </>
                )}
            </p>
            <form className="cd-domain-search" onSubmit={runSearch} role="search" aria-label="Search domains" data-testid="hero-search-form">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try a keyword, brand, or exact match…"
                data-testid="hero-search-input"
                aria-label="Search domains"
              />
              <button type="submit" className="cd-btn cd-btn--primary cd-btn--square" data-testid="hero-search-submit">
                <Search size={18} strokeWidth={2} />
                Search
              </button>
            </form>
            <div className="cd-hero__proof" data-testid="hero-proof">
              {(() => {
                // Auto-defaults derived from live marketplace data. Admin
                // values (from Settings) win when non-empty.
                const autoStats = [
                  { label: "Live inventory", value: mpStats ? Number(mpStats.total_listed).toLocaleString() : "—" },
                  { label: "Moved to date", value: mpStats ? `€${Number(mpStats.value_moved || 0).toLocaleString()}` : "—" },
                  { label: "Avg transfer",  value: "< 24h" },
                ];
                const adminStats = Array.isArray(brand.hero_stats) ? brand.hero_stats : [];
                return autoStats.map((auto, i) => {
                  const admin = adminStats[i] || {};
                  const label = admin.label || auto.label;
                  const value = admin.value || auto.value;
                  return <StatChip key={i} label={label} value={value} accent={i === 1} />;
                });
              })()}
            </div>
          </div>

          <div className="cd-hero__glow" />
        </div>
      </section>

      {/* ===================== 3. FEATURED DOMAINS ===================== */}
      <section id="featured" className="cd-container cd-domain-section" aria-label="Featured domains">
        {searchResults ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20 }}>
            <div>
              <span className="cd-eyebrow" data-testid="featured-eyebrow">
                {searching
                  ? `Searching for "${q}"…`
                  : `Search results · ${searchTotal} ${searchTotal === 1 ? "match" : "matches"} for "${q}"${searchTotal > searchResults.length ? ` · showing top ${searchResults.length}` : ""}`}
              </span>
              <h2 className="cd-h2" style={{ marginTop: 8 }}>
                {searchResults.length > 0 ? "These fit what you're after." : "No matches. Try a different keyword."}
              </h2>
              <p className="cd-lead" style={{ maxWidth: 620, marginTop: 12 }}>
                Every result below is owned outright, priced firmly, and transferable within 24 hours.
              </p>
            </div>
            <button
              type="button"
              onClick={clearSearch}
              className="cd-btn cd-btn--outline cd-btn--sm"
              data-testid="clear-search"
            >
              Clear search
            </button>
          </div>
        ) : null}

        {searchResults ? (
          searchResults.length === 0 && !searching ? (
            <div style={{ marginTop: 36, padding: 60, textAlign: "center", background: "var(--cd-gray-50)", borderRadius: 18, color: "var(--cd-gray-500)" }} data-testid="featured-empty">
              No inventory matches "{q}". Send us a note below and I'll come back with options.
            </div>
          ) : (
            <div className="cd-domain-columns">
              {[
                { title: "Matches", rows: colA },
                { title: "More matches", rows: colB },
                { title: "Also matching", rows: colC },
              ].filter((c) => c.rows.length > 0).map((col) => (
                <div className="cd-domain-card" key={col.title}>
                  <div className="cd-domain-card__head">{col.title}</div>
                  {col.rows.map((d) => (<DomainRow key={d.domain} d={d} />))}
                </div>
              ))}
            </div>
          )
        ) : null}
      </section>

      {/* ===================== 2b. SOLD CASE STUDIES ===================== */}
      {soldCases.length > 0 && (
        <section className="cd-container" aria-label="Recent activity">
          <SoldCaseStudiesStrip rows={soldCases} />
        </section>
      )}

      {/* ===================== 3. CATEGORY CARDS ===================== */}
      <section id="categories" className="cd-container" aria-label="Browse by category">
        <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto" }}>
          <span className="cd-eyebrow">Browse the shelf</span>
          <h2 className="cd-h2" style={{ marginTop: 8 }}>Pick a category, find your fit.</h2>
        </div>
        <div className="cd-category-grid">
          <CategoryCard icon={<Award size={30} strokeWidth={1.6} />} title="Popular" copy="Names buyers are inquiring about right now. Updated daily." />
          <CategoryCard icon={<Zap size={30} strokeWidth={1.6} />} title="3-Letter" copy="Ultra-short, high-signal .com and .io names for founders in a hurry." />
          <CategoryCard icon={<Sparkles size={30} strokeWidth={1.6} />} title="Premium" copy="Exact-match dictionary domains. The top 0.1% of anything we buy." />
        </div>
      </section>

      {/* ===================== 3b. EDITORIAL BAND (top category) ===================== */}
      {topCategory && (
        <section
          aria-label={`${topCategory.key} — most browsed category`}
          data-testid="home-editorial-band"
          style={{
            position: "relative",
            width: "100%",
            marginTop: 24,
            aspectRatio: "16 / 5.4",
            maxHeight: 460,
            minHeight: 260,
            backgroundImage: `url(${topCategory.hero_url})`,
            backgroundSize: "cover",
            backgroundPosition: `${topCategory.focal_x}% ${topCategory.focal_y}%`,
            overflow: "hidden",
          }}
        >
          {/* Left-to-right dark scrim keeps the text legible on any photo */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 85% 15%, rgba(75,111,255,.28), transparent 38%), " +
                "linear-gradient(135deg, rgba(3,20,47,.94) 0%, rgba(7,29,64,.78) 55%, rgba(7,29,64,.35) 100%)",
            }}
          />
          <div
            className="cd-container"
            style={{
              position: "relative",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 18,
              color: "white",
              paddingTop: 40,
              paddingBottom: 40,
            }}
          >
            <span
              className="cd-eyebrow"
              style={{
                color: "rgba(255,255,255,0.75)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#34d399", boxShadow: "0 0 0 4px rgba(52,211,153,0.2)",
                }}
              />
              Case studies · {topCategory.key}
            </span>
            <h2
              className="cd-h2"
              style={{ color: "white", maxWidth: 620, textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}
            >
              {topCategory.tagline}
            </h2>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <Link
                to="/projects"
                className="cd-btn cd-btn--primary"
                data-testid="home-editorial-cta"
                style={{ background: "white", color: "var(--cd-navy-950)" }}
              >
                See the projects we built →
              </Link>
              <span
                style={{
                  fontSize: 11,
                  fontFamily: "ui-monospace, monospace",
                  letterSpacing: ".08em",
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "uppercase",
                }}
              >
                Real brands · built on these names
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ===================== 4. BUYER BENEFITS ===================== */}
      <section className="cd-section cd-section--soft" aria-label="Why buy from CasperDomains">
        <div className="cd-container cd-split">
          <div>
            <span className="cd-eyebrow">For buyers</span>
            <h2 className="cd-h2" style={{ marginTop: 8 }}>A domain deal that closes today, not next quarter.</h2>
            <p className="cd-lead" style={{ marginTop: 16 }}>
              Skip the multi-week broker back-and-forth. Every name on the site is priced, escrow-ready, and pushable within a single business day.
            </p>
            <div className="cd-feature-list">
              <Feature icon={<ShieldCheck strokeWidth={1.5} />} title="Escrow secured over €5,000" body="Escrow.com holds the funds until the domain is safely in your account. Free for you, paid by us." />
              <Feature icon={<Clock strokeWidth={1.5} />} title="Push within 24 hours" body="Payments confirmed before end-of-day usually complete the same evening. Weekends included." />
              <Feature icon={<TrendingUp strokeWidth={1.5} />} title="Room to negotiate" body="Half the inventory closes below asking. Send your number, I'll answer within one business day." />
            </div>
            <Link to="/#featured" className="cd-btn cd-btn--primary" data-testid="benefits-cta">Browse the catalog</Link>
          </div>
          <div className="cd-split__visual">
            <IllustrationDeal />
          </div>
        </div>
      </section>

      {/* ===================== 5. SELLER / INVESTOR PITCH (hidden) ===================== */}
      {false && (
      <section id="sellers" className="cd-section" aria-label="For sellers and investors">
        <div className="cd-container cd-split cd-split--reverse">
          <div>
            <span className="cd-eyebrow">For sellers &amp; investors</span>
            <h2 className="cd-h2" style={{ marginTop: 8 }}>Sitting on a portfolio? Let's move it.</h2>
            <p className="cd-lead" style={{ marginTop: 16 }}>
              I represent a handful of investors selling curated slices of their portfolios through this desk. Reach out with a spreadsheet, I'll come back with a shortlist we can price and list within a week.
            </p>
            <div className="cd-feature-list">
              <Feature icon={<Globe strokeWidth={1.5} />} title="Global buyer flow" body="Founders on four continents, weighted heavily toward US, EU, and Southeast Asia." />
              <Feature icon={<Award strokeWidth={1.5} />} title="15% closing fee" body="No listing fees, no maintenance fees. I only get paid when a domain sells." />
              <Feature icon={<MessageCircle strokeWidth={1.5} />} title="Real broker on every deal" body="Human replies, no auto-responders. Same broker from first email to final push." />
            </div>
            <a href="mailto:hello@casperdomains.com?subject=Portfolio%20consignment" className="cd-btn cd-btn--outline" data-testid="seller-cta">
              Send your list <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="cd-split__visual">
            <IllustrationPortfolio />
          </div>
        </div>
      </section>
      )}

      {/* ===================== 6. TRUST STRIP ===================== */}
      <section className="cd-container" aria-label="Trust badges">
        <div className="cd-trust-grid">
          <TrustItem icon={<ShieldCheck strokeWidth={1.5} />} title="Escrow.com secured" copy="Every deal over €5,000 clears through Escrow.com, so you never wire us blind." />
          <TrustItem icon={<Clock strokeWidth={1.5} />} title="Fast transfers" copy="Push-through in 24 hours max. Same-day for most .com and .io names." />
          <TrustItem icon={<CheckCircle2 strokeWidth={1.5} />} title="Owned outright" copy="Every listing is held on our own registrar accounts, so there are no reserve-price surprises and no third-party holds." />
        </div>
      </section>

      {/* ===================== 7. CTA SEARCH STRIP ===================== */}
      <section className="cd-cta" aria-label="Search domains">
        <div className="cd-container">
          <span className="cd-eyebrow">Still looking?</span>
          <h2 className="cd-h2" style={{ marginTop: 8 }}>Search the inventory now.</h2>
          <form className="cd-domain-search cd-cta__search" onSubmit={runSearch} data-testid="cta-search-form">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. north, verified, market"
              aria-label="Search domains"
              data-testid="cta-search-input"
            />
            <button type="submit" className="cd-btn cd-btn--primary cd-btn--square" data-testid="cta-search-submit">
              <Search size={18} />
              Search
            </button>
          </form>
        </div>
      </section>

      {/* ===================== 8. LEAD CAPTURE ===================== */}
      <LeadCapture />

      <SiteFooter />
      <CompareTray />
      <SnitcherEmbed />
      <BullseyeEmbed page="home" />
    </div>
  );
}

/* --------------------------- sub-components --------------------------- */

/** Featured domain row with gradient thumbnail — deterministic per domain
 *  so the visual identity feels stable across page loads. */
function DomainRow({ d }) {
  const g = gradientFromDomain(d.domain || "");
  const sld = (d.domain || "").split(".")[0];
  const tld = (d.domain || "").split(".").slice(1).join(".");
  const initials = (sld || "?").slice(0, 2).toUpperCase();
  const symbol = d.currency === "USD" ? "$" : "€";
  return (
    <Link
      to={`/l/${d.domain}`}
      className="cd-domain-row"
      data-testid={`featured-row-${d.domain}`}
      style={{ textDecoration: "none", gridTemplateColumns: "44px 1fr auto auto", gap: 12 }}
    >
      <span
        className="cd-domain-thumb"
        aria-hidden
        style={{
          background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
          color: g.ink,
        }}
      >
        {initials}
      </span>
      <span style={{ minWidth: 0, overflow: "hidden" }}>
        <span style={{ display: "block", color: "var(--cd-navy-950)", fontWeight: 650, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sld}
          {tld && <span style={{ color: "var(--cd-gray-500)", fontWeight: 500 }}>.{tld}</span>}
        </span>
        <span className="cd-domain-tld">{tld ? `.${tld} · premium` : "premium"}</span>
      </span>
      <span className="cd-domain-price">
        {d.price ? `${symbol}${Number(d.price).toLocaleString()}` : "Make offer"}
      </span>
      <PinButton listing={d} variant="row" />
    </Link>
  );
}

function CategoryCard({ icon, title, copy }) {
  return (
    <article className="cd-category cd-card" data-testid={`category-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="cd-category__icon">{icon}</div>
      <h3 className="cd-h3" style={{ marginBottom: 6 }}>{title}</h3>
      <p>{copy}</p>
      <Link to="/#featured" className="cd-link">Explore <ArrowUpRight size={12} style={{ display: "inline" }} /></Link>
    </article>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="cd-feature">
      <div className="cd-feature__icon">{icon}</div>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </div>
  );
}

/** Relative-time helper for the activity strips. */
function _rel(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return `${Math.floor(d / 30)}mo ago`;
  } catch { return ""; }
}

/** Anonymized recent-bids feed — no PII, just initials + domain + intent
 *  + amount + relative time. Communicates "the desk is active". */
function RecentBidsStrip({ rows }) {
  return (
    <div data-testid="recent-bids">
      <div className="cd-proof-strip__head">
        <div>
          <h3 className="cd-proof-strip__title">
            <span className="cd-bids__pulse" aria-hidden />
            Recent bids on the desk
          </h3>
          <p className="cd-proof-strip__sub">Live inbound offers · buyer names withheld</p>
        </div>
      </div>
      <div className="cd-bids" style={{ marginTop: 16 }}>
        {rows.map((r, i) => {
          const intentClass = r.intent === "lease" ? "cd-bids__intent--lease"
            : r.intent === "rent" ? "cd-bids__intent--rent" : "";
          return (
            <div className="cd-bids__row" key={i} data-testid={`bid-${i}`}>
              <span className="cd-bids__avatar">{r.initials || "??"}</span>
              <div>
                <span className="cd-bids__domain">{r.domain}</span>
                <span className={`cd-bids__intent ${intentClass}`}>{r.intent || "offer"}</span>
              </div>
              <span className="cd-bids__amount">
                {r.amount ? `${r.currency === "USD" ? "$" : r.currency === "GBP" ? "£" : "€"}${Number(r.amount).toLocaleString()}` : "—"}
              </span>
              <span className="cd-bids__when">{_rel(r.at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Anonymized sold case studies — price band, buyer channel, days-to-close. */
function SoldCaseStudiesStrip({ rows }) {
  return (
    <div data-testid="sold-case-studies">
      <div className="cd-proof-strip__head">
        <div>
          <h3 className="cd-proof-strip__title">Recently closed deals</h3>
          <p className="cd-proof-strip__sub">Anonymized · exact price withheld · channel + timing shown</p>
        </div>
      </div>
      <div className="cd-sold-grid" style={{ marginTop: 16 }}>
        {rows.map((r) => (
          <Link to={`/sold/${r.slug}`} key={r.slug} className="cd-sold-card" data-testid={`sold-${r.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div>
              <div className="cd-sold-card__eyebrow">
                <span className="cd-sold-card__eyebrow-dot" aria-hidden />
                Sold · {_rel(r.sold_at)}
              </div>
              <h4 className="cd-sold-card__domain" style={{ marginTop: 10 }}>{r.domain}</h4>
            </div>
            <div className="cd-sold-card__meta">
              <div>
                <span className="cd-sold-card__meta-label">Price band</span>
                <span className="cd-sold-card__meta-value cd-sold-card__meta-value--price">{r.price_band}</span>
              </div>
              <div>
                <span className="cd-sold-card__meta-label">Days to close</span>
                <span className="cd-sold-card__meta-value">
                  {r.days_to_close == null ? "—" : (r.days_to_close === 0 ? "Same day" : `${r.days_to_close}d`)}
                </span>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <span className="cd-sold-card__meta-label">Buyer</span>
                <span className="cd-sold-card__meta-value">{r.buyer_type}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}


function TrustItem({ icon, title, copy }) {
  return (
    <div className="cd-trust-item">
      <div className="cd-trust-item__icon">{icon}</div>
      <h3>{title}</h3>
      <p>{copy}</p>
    </div>
  );
}

/* Flat vector illustrations (inline SVG — no external assets, respects the
   "same flat vector style but don't copy dan.com assets" brief). */
function IllustrationDeal() {
  return (
    <div className="cd-illus" data-testid="illustration-deal">
      <svg viewBox="0 0 480 360" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="dealGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#4b6fff" />
            <stop offset="1" stopColor="#3ee8d3" />
          </linearGradient>
        </defs>
        <rect x="60" y="90" width="220" height="180" rx="18" fill="#fff" stroke="#eaecf0" strokeWidth="2" />
        <rect x="80" y="120" width="140" height="14" rx="7" fill="#4b6fff" />
        <rect x="80" y="146" width="90" height="10" rx="5" fill="#d0d5dd" />
        <rect x="80" y="200" width="180" height="42" rx="21" fill="url(#dealGrad)" />
        <text x="170" y="227" textAnchor="middle" fill="#03142f" fontFamily="Inter" fontWeight="800" fontSize="16">
          Buy now
        </text>
        <circle cx="360" cy="140" r="60" fill="#e9efff" />
        <path d="M340 140 l14 14 l26 -30" stroke="#365cf5" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="300" y="230" width="120" height="14" rx="7" fill="#eaecf0" />
        <rect x="300" y="252" width="80" height="10" rx="5" fill="#eaecf0" />
      </svg>
    </div>
  );
}

function IllustrationPortfolio() {
  return (
    <div className="cd-illus" data-testid="illustration-portfolio">
      <svg viewBox="0 0 480 360" xmlns="http://www.w3.org/2000/svg">
        <rect x="60" y="60" width="360" height="240" rx="20" fill="#fff" stroke="#eaecf0" strokeWidth="2" />
        <rect x="80" y="88" width="140" height="14" rx="7" fill="#03142f" />
        <rect x="80" y="112" width="90" height="10" rx="5" fill="#d0d5dd" />
        <g fontFamily="Inter" fontSize="12" fill="#475467">
          <rect x="80" y="140" width="320" height="30" rx="8" fill="#f8fafc" />
          <text x="94" y="160">north.io</text>
          <text x="380" y="160" textAnchor="end" fontWeight="700" fill="#12b76a">$8,500</text>
          <rect x="80" y="176" width="320" height="30" rx="8" fill="#f8fafc" />
          <text x="94" y="196">brand.com</text>
          <text x="380" y="196" textAnchor="end" fontWeight="700" fill="#12b76a">$12,000</text>
          <rect x="80" y="212" width="320" height="30" rx="8" fill="#f8fafc" />
          <text x="94" y="232">verified.co</text>
          <text x="380" y="232" textAnchor="end" fontWeight="700" fill="#12b76a">$3,200</text>
          <rect x="80" y="248" width="320" height="30" rx="8" fill="#e7fbf8" />
          <text x="94" y="268" fill="#03142f" fontWeight="700">SOLD · casper.tld</text>
          <text x="380" y="268" textAnchor="end" fontWeight="700" fill="#25d6c5">✓ closed</text>
        </g>
      </svg>
    </div>
  );
}

/* --------------------------- lead capture --------------------------- */

function LeadCapture() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | ok
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) {
      toast.error("Please add an email and a note.");
      return;
    }
    setStatus("sending");
    try {
      await submitLead({
        ...form,
        source: "public_home",
        session_id: localStorage.getItem("sniper_sid") || null,
      });
      setStatus("ok");
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent, you'll hear back within a business day.");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Try email direct: hello@casperdomains.com");
    }
  };

  return (
    <section className="cd-section cd-section--soft" aria-label="Contact the broker">
      <div className="cd-container cd-split">
        <div>
          <span className="cd-eyebrow">Contact</span>
          <h2 className="cd-h2" style={{ marginTop: 8 }}>Get in touch</h2>
          <p className="cd-lead" style={{ marginTop: 16 }}>
            Buying a name, making an offer, or selling a portfolio — use the form and we reply personally within one business day, usually much sooner.
          </p>
          <div style={{ display: "flex", gap: 20, alignItems: "center", marginTop: 24, flexWrap: "wrap", color: "var(--cd-gray-600)", fontSize: 14 }}>
            <a href="mailto:hello@casperdomains.com" className="cd-link">hello@casperdomains.com</a>
            <span>·</span>
            <span>Rotterdam, NL</span>
          </div>
        </div>
        <form
          className="cd-payment-card"
          onSubmit={submit}
          data-testid="lead-form"
          style={{ maxWidth: 480 }}
        >
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--cd-gray-700)", marginBottom: 6 }}>Your name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cd-input" placeholder="Jane Doe" data-testid="lead-name" />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--cd-gray-700)", marginBottom: 6, marginTop: 16 }}>Email</label>
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" className="cd-input" placeholder="you@company.com" required data-testid="lead-email" />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--cd-gray-700)", marginBottom: 6, marginTop: 16 }}>Message</label>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="cd-input" style={{ height: 130, padding: 14, resize: "vertical" }} placeholder="I'm looking for a 2-word .com under $10K in the fintech space…" required data-testid="lead-message" />

          <button className="cd-btn cd-btn--primary cd-btn--full" style={{ marginTop: 20 }} disabled={status === "sending"} data-testid="lead-submit">
            {status === "sending" ? "Sending…" : status === "ok" ? "Sent · will reply soon" : "Send the request"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* Add .cd-input global style locally (the design system CSS defines it too, but
   we make sure the input+textarea share the same base). */

function StatChip({ label, value, accent = false }) {
  return (
    <div
      className="cd-hero__stat-chip"
      data-testid={`hero-stat-chip-${label.replace(/\s+/g, "-")}`}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        gap: 2,
        padding: "10px 18px 10px 0",
        borderRight: "1px solid rgba(255,255,255,.14)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--cd-font)",
          fontWeight: 800,
          fontSize: 18,
          letterSpacing: "-0.02em",
          color: accent ? "var(--cd-cyan-400)" : "#fff",
        }}
      >
        {value}
      </span>
      <span style={{ fontSize: 11, color: "rgba(255,255,255,.6)", letterSpacing: ".04em", textTransform: "uppercase", fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
}

/** Right-panel domain orb — replaces the placeholder "north.io" fake copy
 *  with a real featured domain. Three orbs rotate on the same orbital ring;
 *  each is a real listing so clicking jumps straight into that lander. */
function HeroOrb({ d, idx }) {
  const sym = d.currency === "USD" ? "$" : "€";
  const g = gradientFromDomain(d.domain || d.slug || "");
  const sizes = ["cd-hero__orb--a", "cd-hero__orb--b", "cd-hero__orb--c"];
  const label = idx === 1
    ? (d.price ? `${sym}${Number(d.price).toLocaleString()}` : "make offer")
    : (d.domain || d.slug);
  return (
    <Link
      to={`/l/${d.slug || d.domain}`}
      className={`cd-hero__orb ${sizes[idx] || ""}`}
      data-testid={`hero-orb-${idx}`}
      style={{
        background: `linear-gradient(135deg, ${g.from}, ${g.to})`,
        color: g.ink,
        textDecoration: "none",
        fontSize: idx === 1 ? 20 : 15,
        fontWeight: idx === 1 ? 800 : 700,
      }}
    >
      {label}
    </Link>
  );
}

// -----------------------------------------------------------------
// Hero WOW helpers
// -----------------------------------------------------------------

/** Rotates through the admin-authored (or default) list of aspirational
 *  nouns underneath the H1's article, one every 2.6s. Falls back to the
 *  built-in list when no admin string is provided. */
function RotatingWord({ words }) {
  const DEFAULT = ["next big idea", "SaaS launch", "brand exit", "AI product"];
  const list = Array.isArray(words) && words.length > 0 ? words : DEFAULT;
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (list.length <= 1) return;
    const t = setInterval(() => setI((v) => (v + 1) % list.length), 2600);
    return () => clearInterval(t);
  }, [list.length]);
  return (
    <span key={i} className="cd-hero__word-flip-inner" data-testid="hero-rotating-word">
      {list[i]}
    </span>
  );
}

